import { useWatch } from '@vtex/admin-ui-form'
import { useCallback, useEffect } from 'react'
import {
  Attributes,
  type MaxItemPerCart as MaxItemPerCartType,
  type MaxItemsPerAttribute as MaxItemsPerAttributeType,
  type Restrictions,
} from 'typings'
import { useFormStateContext } from 'ui'
import {
  ApplyTo,
  RestrictionsMap,
} from 'ui/components/PromotionForm/Restrictions/typings'
import { RegularBenefitVariant } from '../../../../typings'
import {
  useRestrictionsContext,
  useRestrictionVisibility,
} from 'ui/components/PromotionForm/Restrictions/hooks/useRestrictions.constate'

export const benefitTypeByRestrictions: Partial<
  Record<RestrictionsMap, Array<RegularBenefitVariant>>
> = {
  [RestrictionsMap.MAX_ITEMS_PER_ATTRIBUTE]: [RegularBenefitVariant.Percentual],
  [RestrictionsMap.MAX_ITEMS_PER_CART]: [
    RegularBenefitVariant.Nominal,
    RegularBenefitVariant.Formula,
    RegularBenefitVariant.MaxPrice,
  ],
}

type Props = {
  name: string
}

/**
 * Hook to handle restrictions visibility based on the benefit type.
 *
 * Max items per attribute is only visible for percentual benefit.
 * Max items per cart is only visible for nominal, formula or max price benefit.
 * All other restrictions are always visible.
 * Attribute and cart restrictions are mutually exclusive, so when hiding one, we propagate the data to the other.
 */
export function useRestrictionsByBenefit(props: Props) {
  const { name } = props

  const { form } = useFormStateContext()

  const { registeredRestrictions: restrictions } = useRestrictionsContext()
  const { hide, show } = useRestrictionVisibility()

  const benefit: RegularBenefitVariant = useWatch({ form, name })

  const isEnabled: (name: string) => boolean = useCallback(
    (name: string) => {
      if (Object.keys(benefitTypeByRestrictions).includes(name)) {
        return benefitTypeByRestrictions[name]?.includes(benefit)
      }

      return true
    },
    [benefit]
  )

  function isMaxItemsPerCart(
    name: string,
    value: Restrictions[keyof Restrictions]
  ): value is MaxItemPerCartType {
    return name === RestrictionsMap.MAX_ITEMS_PER_CART
  }

  function isMaxItemsPerAttribute(
    name: string,
    value: Restrictions[keyof Restrictions]
  ): value is MaxItemsPerAttributeType {
    return name === RestrictionsMap.MAX_ITEMS_PER_ATTRIBUTE
  }

  const propagateCartValue = useCallback(
    (name: string, config: Restrictions[keyof Restrictions]) => {
      if (
        isMaxItemsPerCart(name, config) &&
        isEnabled(RestrictionsMap.MAX_ITEMS_PER_ATTRIBUTE)
      ) {
        form.setValue(
          restrictions[RestrictionsMap.MAX_ITEMS_PER_ATTRIBUTE].path,
          {
            attribute: Attributes.CART,
            value: config.value,
            applyTo: config.applyTo ?? ApplyTo.cheapest,
          }
        )
      } else if (
        isMaxItemsPerAttribute(name, config) &&
        config.attribute === Attributes.CART &&
        isEnabled(RestrictionsMap.MAX_ITEMS_PER_CART)
      ) {
        form.setValue(restrictions[RestrictionsMap.MAX_ITEMS_PER_CART].path, {
          value: config.value,
          applyTo: config.applyTo,
        })
      }
    },
    [form, isEnabled, restrictions]
  )

  useEffect(() => {
    if (!Object.keys(restrictions).length) return

    Object.keys(restrictions).forEach((name) => {
      const { hidden, path } = restrictions[name]

      if (isEnabled(name)) {
        if (hidden) {
          show(name)
        }
      } else {
        const value = form.getValues(path)
        if (value) {
          propagateCartValue(name, value)
          form.setValue(path, undefined)
        }
        if (!hidden) {
          hide(name)
        }
      }
    })
  }, [form, hide, restrictions, isEnabled, show, propagateCartValue])
}
